import axios, { AxiosHeaders } from 'axios';
import { auth } from '../firebase';
import { isLocalE2EAuthEnabled, localE2EBearerToken } from '../localE2eAuth';

// Use environment variable if set, otherwise default to localhost for local development
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';

const api = axios.create({ baseURL: API_BASE_URL });

export class AuthTokenUnavailableError extends Error {
  constructor(code) {
    super('Your sign-in could not be verified. Refresh the page or sign in again, then retry.');
    this.name = 'AuthTokenUnavailableError';
    if (code) this.code = code;
  }
}

// Resolve the token per request: Firebase refreshes its ID token without firing onAuthStateChanged,
// so any cached Authorization header goes stale. No 401 retry: a retried mutation could apply twice.
export const attachCurrentAuthToken = async (config) => {
  const headers = AxiosHeaders.from(config.headers);
  headers.delete('Authorization');
  let token = null;
  if (isLocalE2EAuthEnabled()) {
    token = localE2EBearerToken();
  } else {
    try {
      if (typeof auth.authStateReady === 'function') await auth.authStateReady();
      const user = auth.currentUser;
      token = user ? await user.getIdToken() : null;
    } catch (err) {
      throw new AuthTokenUnavailableError(typeof err?.code === 'string' ? err.code : undefined);
    }
  }
  if (token) headers.set('Authorization', `Bearer ${token}`);
  config.headers = headers;
  return config;
};

api.interceptors.request.use(attachCurrentAuthToken);

export const projectsAPI = { 
  getAll: () => api.get('/projects'), 
  create: (d) => api.post('/projects', d) 
};

export const cfoAPI = {
  getInsights: () => api.get('/analytics/cfo-insights')
};

/** Include orgId only when a real value is supplied; omit otherwise so the backend resolves from auth. */
const orgIdParams = (orgId) => (orgId != null && orgId !== "" ? { orgId } : {});

export const cfoInsightsAPI = {
  getSummary:     (orgId) => api.get("/cfo-insights/summary", { params: orgIdParams(orgId) }),
  getArAp:        (orgId) => api.get("/cfo-insights/arap", { params: orgIdParams(orgId) }),
  getCashFlow:    (orgId) => api.get("/cfo-insights/cashflow", { params: orgIdParams(orgId) }),
  getAging:       (orgId) => api.get("/cfo-insights/aging", { params: orgIdParams(orgId) }),
  getMargin:      (orgId) => api.get("/cfo-insights/margin", { params: orgIdParams(orgId) }),
  getBankability: (orgId) => api.get("/cfo-insights/bankability", { params: orgIdParams(orgId) }),
  getSnapshots:   (orgId) => api.get("/cfo-insights/snapshots", { params: orgIdParams(orgId) }),
};

export const tradePartnerAPI = {
  getDashboard: (tradePartnerId) => api.get(`/trade-partner/dashboard?tradePartnerId=${tradePartnerId}`)
};

export const gcRoiAPI = {
  getSummary:       ()         => api.get('/gc-roi/summary'),
  getDrilldown:     (metricKey) => api.get(`/gc-roi/drilldown?metricKey=${encodeURIComponent(metricKey)}`),
  getProjectApprovals: ()      => api.get('/gc-roi/project-approvals'),
  searchTradePartners: (q)     => api.get('/gc-roi/trade-partners', { params: { q } }),
  getQueue:         ()         => api.get('/gc-roi/introduction-queue'),
  addToQueue:       (payload)  => api.post('/gc-roi/introduction-queue', payload),
  addSourcePendingToQueue: (payload) => api.post('/gc-roi/introduction-queue/source-pending', payload),
  createCandidateDisposition: (payload)  => api.post('/gc-roi/candidate-dispositions', payload),
  getActiveCandidateDispositions: () => api.get('/gc-roi/candidate-dispositions/active'),
  getCandidateDispositionHistory: (vendorId) => api.get('/gc-roi/candidate-dispositions/history', { params: { vendorId } }),
  updateQueueItem:  (id, data) => api.put(`/gc-roi/introduction-queue/${id}`, data),
  removeFromQueue:  (id)       => api.delete(`/gc-roi/introduction-queue/${id}`),
};

// API_BASE_URL already includes `/api`; these paths resolve to the authenticated
// server-authoritative `/api/v1/bondready/**` namespace.
export const bondReadyAPI = {
  createGcSponsored:   (data)     => api.post('/v1/bondready/opportunities/gc-sponsored', data),
  createDirectCustomer:(data)     => api.post('/v1/bondready/opportunities/direct-customer', data),
  listOpportunities:   ()         => api.get('/v1/bondready/opportunities'),
  getPipelineSummary:  ()         => api.get('/v1/bondready/pipeline-summary'),
  getOpportunity:      (id)       => api.get(`/v1/bondready/opportunities/${id}`),
  updateWorkflow:      (id, data) => api.put(`/v1/bondready/opportunities/${id}/workflow`, data),
  decideGate1:         (id, data) => api.post(`/v1/bondready/opportunities/${id}/authorizations/gate-1`, data),
  getGate2:            (id)       => api.get(`/v1/bondready/opportunities/${id}/authorizations/gate-2`),
  decideGate2:         (id, data) => api.post(`/v1/bondready/opportunities/${id}/authorizations/gate-2`, data),
  getDisclosurePolicy: (id)       => api.get(`/v1/bondready/opportunities/${id}/disclosure-policy`),
  createBondRequest:   (id, data) => api.post(`/v1/bondready/opportunities/${id}/requests/bond`, data),
  createFundingRequest:(id, data) => api.post(`/v1/bondready/opportunities/${id}/requests/funding`, data),
  updateSupportRequest:(id, requestId, data) => api.put(`/v1/bondready/opportunities/${id}/requests/${requestId}`, data),
  getConfiguredBondPartner: () => api.get('/v1/bondready/bond-request/configured-partner'),
  grantConfiguredBondPartnerVisibility: (id, data) =>
    api.post(`/v1/bondready/opportunities/${id}/partner-visibility/configured-bond-request-partner`, data || {}),
  submitBondRequest: (opportunityId, requestId, data) =>
    api.post(
      `/v1/bondready/opportunities/${opportunityId}/requests/${requestId}/submit-bond-request`,
      data || {}
    ),
  listInformationRequests: (opportunityId, requestId) =>
    api.get(`/v1/bondready/opportunities/${opportunityId}/requests/${requestId}/information-requests`),
  createInformationRequest: (opportunityId, requestId, data) =>
    api.post(
      `/v1/bondready/opportunities/${opportunityId}/requests/${requestId}/information-requests`,
      data || {}
    ),
  respondToInformationRequest: (opportunityId, informationRequestId, data) =>
    api.post(
      `/v1/bondready/opportunities/${opportunityId}/information-requests/${informationRequestId}/responses`,
      data || {}
    ),
  uploadInformationAttachment: (opportunityId, informationRequestId, itemId, file, localTestRunId) => {
    const form = new FormData();
    form.append('file', file);
    const params = localTestRunId ? { localTestRunId } : undefined;
    return api.post(
      `/v1/bondready/opportunities/${opportunityId}/information-requests/${informationRequestId}/items/${itemId}/attachments`,
      form,
      { params, headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },
  listInformationAttachments: (opportunityId, informationRequestId, itemId) =>
    api.get(
      `/v1/bondready/opportunities/${opportunityId}/information-requests/${informationRequestId}/items/${itemId}/attachments`
    ),
  deleteInformationAttachment: (opportunityId, attachmentId) =>
    api.delete(`/v1/bondready/opportunities/${opportunityId}/information-attachments/${attachmentId}`),
  downloadInformationAttachment: (opportunityId, attachmentId, disposition = 'attachment') =>
    api.get(
      `/v1/bondready/opportunities/${opportunityId}/information-attachments/${attachmentId}/content`,
      { params: { disposition }, responseType: 'blob' }
    ),
  getAttachmentCapability: () => api.get('/v1/bondready/opportunities/attachments/capability'),
  refreshInformationAttachmentScan: (opportunityId, attachmentId) =>
    api.post(`/v1/bondready/opportunities/${opportunityId}/information-attachments/${attachmentId}/scan`),
  revokeInformationAttachment: (opportunityId, attachmentId, reason) =>
    api.post(
      `/v1/bondready/opportunities/${opportunityId}/information-attachments/${attachmentId}/revoke`,
      reason ? { reason } : {}
    ),
};

export default api;